import { gql } from '@apollo/client';
import Honeybadger from 'honeybadger-js';
import { useState } from 'react';
import { useCurrentPressroom } from '../contexts/CurrentPressroomContext';
import { useEnvironmentVariables } from '../contexts/EnvironmentVariablesContext';
import { useSyncCloudinaryDocumentMutation } from '../generated/hooks';
import type { SyncCloudinaryDocumentMutation } from '../generated/types';

gql`
  mutation SyncCloudinaryDocument(
    $pressroomId: ID!
    $documentId: ID
    $cloudinaryId: ID!
    $cloudinaryVersion: Int!
    $cloudinaryFormat: String!
    $cloudinaryBytes: Int!
    $fileName: String!
  ) {
    documentSyncCloudinaryUpload(
      input: {
        pressroomId: $pressroomId
        cloudinaryId: $cloudinaryId
        cloudinaryVersion: $cloudinaryVersion
        documentId: $documentId
        cloudinaryFormat: $cloudinaryFormat
        cloudinaryBytes: $cloudinaryBytes
        fileName: $fileName
      }
    ) {
      error {
        message
      }
      document {
        id
        header
        resourceDocument {
          id
          cloudinaryId
          name
        }
      }
    }
  }
`;

interface DocumentUploadResponse {
  asset_id: string;
  public_id: string;
  version: number;
  version_id: string;
  signature: string;
  width: number;
  height: number;
  format?: string;
  resource_type: 'image' | 'raw';
  created_at: string;
  tags: string[];
  pages: number;
  bytes: number;
  type: 'upload';
  etag: string;
  placeholder: false;
  url: string;
  secure_url: string;
  access_mode: 'public';
  original_filename: string;
  original_extension: string;
}
type DocumentUploadErrorResponse = {
  error?: {
    message?: string;
  };
};
type CallFunction = (
  file: Blob,
  documentId?: string,
) => Promise<SyncCloudinaryDocumentMutation | null>;

type State = {
  loading: boolean;
  error: boolean;
  data?: SyncCloudinaryDocumentMutation | null;
};

type Args = {
  onError?: (error?: string) => void;
  onCompleted?: (response: SyncCloudinaryDocumentMutation) => void;
};

const constructFormData = (
  file: Blob,
  sourceId: string,
  { apiKey, preset }: { apiKey: string; preset: string },
) => {
  const fd = new FormData();

  fd.append('api_key', apiKey);
  fd.append('timestamp', (Date.now() / 1000).toString(10));
  fd.append('upload_preset', preset);
  fd.append('resource_type', 'document');
  fd.append('file', file);
  fd.append('tags', [`source_${sourceId}`] as any);
  return fd;
};

/**
 * Uploads a blob to cloudinary, and then lets backend know about the created image.
 */
export const useCloudinaryDocumentUpload = ({
  onError,
  onCompleted,
}: Args = {}): [CallFunction, State] => {
  const { mndAssets } = useEnvironmentVariables();
  const { sourceId, pressroomId } = useCurrentPressroom();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [syncDatabase, { loading: syncLoading, error: syncError, data }] =
    useSyncCloudinaryDocumentMutation({ onCompleted });

  return [
    async (file: Blob, documentId?: string) => {
      setError(false);
      const fd = constructFormData(file, sourceId, mndAssets);

      setLoading(true);
      try {
        const response = await fetch(mndAssets.postUrl?.replace('image', 'auto'), {
          method: 'POST',
          body: fd,
        });
        setLoading(false);
        if (!response.ok) {
          const returnValue = (await response.json()) as DocumentUploadErrorResponse;
          onError?.(returnValue?.error?.message);
          setError(true);
          Honeybadger.notify(response);
          return null;
        }
        const returnValue = (await response.json()) as DocumentUploadResponse;

        let cloudinaryFormat = returnValue.format ?? '';

        const fileNameFromFile = (file as any).name as string;

        if (!cloudinaryFormat) {
          const fileName = fileNameFromFile || returnValue.public_id;
          const n = fileName.lastIndexOf('.');
          cloudinaryFormat = fileName.slice(n - (fileName.length - 1));
        }

        // Cloudinary does for somereason rewrite some pdf's into ai. We need to fix that.
        if (cloudinaryFormat === 'ai') {
          cloudinaryFormat = returnValue.original_extension;
        }

        const gqlResponse = await syncDatabase({
          variables: {
            pressroomId,
            documentId,
            cloudinaryId: returnValue.public_id,
            cloudinaryVersion: returnValue.version,
            cloudinaryBytes: returnValue.bytes,
            cloudinaryFormat,
            fileName: fileNameFromFile || returnValue.original_filename || ' ',
          },
        });
        return gqlResponse.data || null;
      } catch (e) {
        onError?.();
        console.error(e);
        setError(true);
        setLoading(false);
        return null;
      }
    },
    { loading: loading || syncLoading, error: error || !!syncError, data },
  ];
};
